import { useState, useCallback } from "react";

interface LiteYouTubeProps {
  videoId: string;
  title: string;
  className?: string;
}

/**
 * Lightweight YouTube facade – renders a thumbnail + play button.
 * The real iframe loads only after the user clicks, eliminating
 * ~800 KB of YouTube JS from the initial page load.
 */
export default function LiteYouTube({ videoId, title, className = "" }: LiteYouTubeProps) {
  const [activated, setActivated] = useState(false);

  const handleClick = useCallback(() => setActivated(true), []);

  if (activated) {
    return (
      <iframe
        className={className}
        src={`https://www.youtube.com/embed/${videoId}?autoplay=1`}
        title={title}
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        referrerPolicy="strict-origin-when-cross-origin"
        allowFullScreen
      />
    );
  }

  return (
    <button
      type="button"
      className={`relative cursor-pointer bg-black group ${className}`}
      onClick={handleClick}
      aria-label={`Play: ${title}`}
    >
      <img
        src={`https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`}
        alt={title}
        className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
        loading="lazy"
        width={480}
        height={360}
      />
      {/* YouTube-style play button */}
      <div className="absolute inset-0 flex items-center justify-center">
        <svg
          className="h-16 w-16 text-white drop-shadow-lg group-hover:scale-110 transition-transform"
          viewBox="0 0 68 48"
          aria-hidden="true"
        >
          <path
            d="M66.52 7.74c-.78-2.93-2.49-5.41-5.42-6.19C55.79.13 34 0 34 0S12.21.13 6.9 1.55C3.97 2.33 2.27 4.81 1.48 7.74.06 13.05 0 24 0 24s.06 10.95 1.48 16.26c.78 2.93 2.49 5.41 5.42 6.19C12.21 47.87 34 48 34 48s21.79-.13 27.1-1.55c2.93-.78 4.64-3.26 5.42-6.19C67.94 34.95 68 24 68 24s-.06-10.95-1.48-16.26z"
            fill="red"
          />
          <path d="M45 24 27 14v20" fill="white" />
        </svg>
      </div>
    </button>
  );
}
